import { useCallback, useEffect, useState } from "react";
import {
  activatePricingModel,
  deactivatePricingModel,
  fetchAdminPricingModels,
  trainPricingModel,
} from "../../../../services/vehiclePredictivePricingAdminApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import { formatCop } from "../../mobility/vehicleMarketFormat";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const VehiclePricingModelsAdminPage = ({ onClose }) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [training, setTraining] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminPricingModels();
      setModels(data.models || []);
    } catch (err) {
      setError(err.message || "Unable to load pricing models.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleTrain = async () => {
    setTraining(true);
    setToast("");
    try {
      const r = await trainPricingModel({ activate: true });
      setToast(
        r.ok
          ? `Trained model ${r.model?.modelVersion || ""} (${r.trainingRowCount ?? 0} rows).`
          : r.message || "Training failed."
      );
      load();
    } catch (err) {
      setToast(err.message || "Training failed.");
    } finally {
      setTraining(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      await activatePricingModel(id);
      setToast("Model activated.");
      load();
    } catch (err) {
      setToast(err.message || "Activation failed.");
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await deactivatePricingModel(id);
      setToast("Model deactivated.");
      load();
    } catch (err) {
      setToast(err.message || "Deactivation failed.");
    }
  };

  return (
    <div className="admin-dashboard admin-vehicle-pricing-models admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <h1>Pricing Models</h1>
          <p className="muted">Registry and training for Fair Value v2 predictive models.</p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button
            type="button"
            className="crm-btn crm-btn-primary"
            onClick={handleTrain}
            disabled={training}
          >
            {training ? "Training…" : "Train new model"}
          </button>
          {onClose ? (
            <button type="button" className="crm-btn crm-btn-secondary" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {toast ? <p className="admin-toast">{toast}</p> : null}
      {loading ? <AdminStatePanel state="loading" message="Loading models…" /> : null}
      {error ? <AdminStatePanel state="error" message={error} onRetry={load} /> : null}

      {!loading && !error ? (
        <AdminTableContainer title="Model registry">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Version</th>
                <th>Type</th>
                <th>Status</th>
                <th>Rows</th>
                <th>MAE</th>
                <th>MAPE</th>
                <th>RMSE</th>
                <th>R²</th>
                <th>Active</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {models.length === 0 ? (
                <tr>
                  <td colSpan={12} className="muted">
                    No models registered. Train the first weighted-feature model to begin.
                  </td>
                </tr>
              ) : (
                models.map((m) => (
                  <tr key={m.id}>
                    <td>{m.modelKey}</td>
                    <td>{m.modelVersion}</td>
                    <td>{m.modelType}</td>
                    <td>{m.trainingStatus}</td>
                    <td>{m.trainingRowCount ?? "—"}</td>
                    <td>{m.maeCop != null ? formatCop(m.maeCop) : "—"}</td>
                    <td>{m.mapePct != null ? `${m.mapePct}%` : "—"}</td>
                    <td>{m.rmseCop != null ? formatCop(m.rmseCop) : "—"}</td>
                    <td>{m.r2Score ?? "—"}</td>
                    <td>{m.isActive ? "Yes" : "No"}</td>
                    <td>{formatDate(m.createdAt)}</td>
                    <td className="admin-table__actions">
                      {!m.isActive && m.trainingStatus === "SUCCESS" ? (
                        <button
                          type="button"
                          className="crm-btn crm-btn-ghost"
                          onClick={() => handleActivate(m.id)}
                        >
                          Activate
                        </button>
                      ) : null}
                      {m.isActive ? (
                        <button
                          type="button"
                          className="crm-btn crm-btn-ghost"
                          onClick={() => handleDeactivate(m.id)}
                        >
                          Deactivate
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}
    </div>
  );
};

export default VehiclePricingModelsAdminPage;
